/**
 * RouteCalculationToast Component
 *
 * Persistent toast notification for route calculation progress.
 * Displays phase-based status, progress bar, and helpful subtext.
 * Stays visible across tab navigation.
 */

import React from 'react';
import { X, MapPin, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/routes';

/**
 * Calculation phases with user-friendly messages
 */
type CalculationPhase =
  | 'idle'
  | 'sending_request'
  | 'loading_campaign'
  | 'geocoding_addresses'
  | 'calculating_routes'
  | 'optimizing'
  | 'complete';

const PHASE_MESSAGES: Record<CalculationPhase, string> = {
  idle: 'Ready',
  sending_request: 'Sending request...',
  loading_campaign: 'Loading campaign data...',
  geocoding_addresses: 'Geocoding addresses...',
  calculating_routes: 'Calculating routes...',
  optimizing: 'Optimizing route order...',
  complete: 'Route calculation complete!',
};

interface RouteCalculationToastProps {
  /** Whether calculation is in progress */
  isCalculating: boolean;
  /** Current phase of calculation */
  phase: CalculationPhase;
  /** Progress percentage (0-100) */
  progress: number;
  /** Elapsed time in seconds */
  elapsedSeconds: number;
  /** Error message if calculation failed */
  error: string | null;
  /** Whether calculation just completed (for success state) */
  isComplete: boolean;
  /** Callback to dismiss the toast */
  onDismiss: () => void;
}

/**
 * RouteCalculationToast - Persistent progress notification
 *
 * @example
 * ```tsx
 * <RouteCalculationToast
 *   isCalculating={true}
 *   phase="geocoding_addresses"
 *   progress={45}
 *   elapsedSeconds={30}
 *   error={null}
 *   isComplete={false}
 *   onDismiss={() => dismissToast()}
 * />
 * ```
 */
export const RouteCalculationToast: React.FC<RouteCalculationToastProps> = ({
  isCalculating,
  phase,
  progress,
  elapsedSeconds,
  error,
  isComplete,
  onDismiss,
}) => {
  const navigate = useNavigate();

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle "View Route" action
  const handleViewRoute = () => {
    navigate(ROUTES.ROUTES);
    onDismiss();
  };

  // Error state
  if (error) {
    return (
      <div className="bg-dark-surface border border-red-500/50 rounded-xl shadow-2xl shadow-black/20 p-4 min-w-[300px] max-w-[380px]">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-red-500/20 rounded-full">
            <AlertCircle className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-red-400">Route Calculation Failed</h4>
              <button
                onClick={onDismiss}
                className="p-1.5 rounded-full hover:bg-dark-surface/50 active:bg-dark-header transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Dismiss"
                data-interaction-id='button-dismiss'>
                <X className="w-4 h-4 text-dark-muted" />
              </button>
            </div>
            <p className="text-xs text-dark-muted mt-1 line-clamp-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Success/Complete state
  if (isComplete && !isCalculating) {
    return (
      <div className="bg-dark-surface border border-green-500/50 rounded-xl shadow-2xl shadow-black/20 p-4 min-w-[300px] max-w-[380px]">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-green-500/20 rounded-full">
            <Check className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-green-400">Route Calculation Complete!</h4>
              <button
                onClick={onDismiss}
                className="p-1.5 rounded-full hover:bg-dark-surface/50 active:bg-dark-header transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Dismiss"
                data-interaction-id='button-dismiss'>
                <X className="w-4 h-4 text-dark-muted" />
              </button>
            </div>
            <p className="text-xs text-dark-muted mt-1">
              Completed in {formatTime(elapsedSeconds)}
            </p>
            <button
              onClick={handleViewRoute}
              className="mt-2 px-3 py-2 text-xs font-medium text-brand-500 bg-brand-500/10 border border-brand-500/30 rounded hover:bg-brand-500/20 active:bg-brand-500/30 transition-all duration-150 min-h-[44px]"
              data-interaction-id='button-view-route'>
              View Route
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Progress state (calculating)
  if (isCalculating) {
    return (
      <div className="bg-dark-surface border border-brand-500/50 rounded-xl shadow-2xl shadow-black/20 p-4 min-w-[300px] max-w-[380px]">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-brand-500/20 rounded-full">
            <Loader2 className="w-5 h-5 text-brand-400 animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-brand-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Calculating Route...
              </h4>
              <button
                onClick={onDismiss}
                className="p-1.5 rounded-full hover:bg-dark-surface/50 active:bg-dark-header transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Dismiss"
                data-interaction-id='button-dismiss'>
                <X className="w-4 h-4 text-dark-muted" />
              </button>
            </div>

            {/* Phase message */}
            <p className="text-xs text-dark-text mt-1">
              {PHASE_MESSAGES[phase] || 'Processing...'}
            </p>

            {/* Progress bar */}
            <div className="mt-2 h-2 bg-dark-surface/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 transition-all duration-200 ease-out"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>

            {/* Progress percentage and elapsed time */}
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-brand-400 font-medium">{progress}%</span>
              <span className="text-xs text-dark-muted">{formatTime(elapsedSeconds)}</span>
            </div>

            {/* Helpful subtext */}
            <p className="text-[11px] text-dark-muted mt-2 italic">
              You can safely navigate to other tabs while this completes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Idle state - don't render anything
  return null;
};

export default RouteCalculationToast;
